// iter 398 — Mutation guard: stops background refetches / WS deltas from
// painting stale server data over a row the user just changed.
//
// Jake: "I switched it and then it popped back up, and then after a couple
// seconds it went away. That needs to be spotless." The race: optimistic
// update → an in-flight or WS-triggered refetch resolves with the OLD row
// (server cache not yet busted, or the request started before the write)
// → row snaps back → the next refetch fixes it. Rows removed optimistically
// (Delivered / Canceled) had no `_syncing` marker to protect them at all.
//
// Usage:
//   beginMutation(id, { removed: true })   // before the request
//   endMutation(id)                        // on success (keeps a short grace)
//   cancelMutation(id)                     // on failure (then refetch)
//   guardRows(nextRows, prevRows)          // in every refetch merge
const GRACE_MS = 5000;
const _guards = new Map(); // id -> { until: number|null, removed: boolean }

function _ids(row) {
  const out = [];
  if (row?.id) out.push(row.id);
  if (row?.airtable_id) out.push(row.airtable_id);
  return out;
}

function _sweep() {
  const now = Date.now();
  for (const [id, g] of _guards) {
    if (g.until !== null && g.until <= now) _guards.delete(id);
  }
}

export function beginMutation(id, { removed = false } = {}) {
  if (!id) return;
  _guards.set(id, { until: null, removed });
}

export function endMutation(id, { grace = GRACE_MS } = {}) {
  if (!id) return;
  const g = _guards.get(id);
  if (g) g.until = Date.now() + grace;
}

export function cancelMutation(id) {
  if (id) _guards.delete(id);
}

export function isGuarded(id) {
  _sweep();
  return !!id && _guards.has(id);
}

export function isRemoved(id) {
  _sweep();
  return !!id && !!_guards.get(id)?.removed;
}

// Merge fresh server rows with the current rows: guarded (or `_syncing`)
// rows keep their local copy; optimistically-removed rows stay gone.
export function guardRows(nextRows, prevRows = []) {
  _sweep();
  if (!Array.isArray(nextRows)) return nextRows;
  if (_guards.size === 0 && !prevRows.some((r) => r?._syncing)) return nextRows;
  const prevById = new Map();
  for (const r of prevRows) for (const k of _ids(r)) prevById.set(k, r);
  const out = [];
  for (const nr of nextRows) {
    const keys = _ids(nr);
    if (keys.some(isRemoved)) continue;
    const local = keys.map((k) => prevById.get(k)).find(Boolean);
    if (local && (local._syncing || keys.some(isGuarded))) {
      out.push(local);
    } else {
      out.push(nr);
    }
  }
  return out;
}
